import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Sun, EnvelopeSimple, CalendarCheck, Warning, Clock, Moon, ArrowClockwise } from "@phosphor-icons/react";

const KIND_META = {
  reply: { label: "replied", color: "var(--accent)", icon: EnvelopeSimple },
  meeting: { label: "meeting", color: "#34C759", icon: CalendarCheck },
  overdue_task: { label: "overdue", color: "#FF3B30", icon: Warning },
  today_task: { label: "today", color: "#FF9500", icon: Clock },
  dormant: { label: "dormant", color: "#8E8E93", icon: Moon },
};

export default function DailyBriefing() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);

  const load = useCallback(async (regen = false) => {
    const setter = regen ? setReloading : setLoading;
    setter(true);
    try {
      const r = await api.get(`/ai/briefing?narrate=true`);
      setBrief(r.data);
    } catch (e) {
      setBrief({ error: e.response?.data?.detail || e.message });
    } finally {
      setter(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  if (loading) {
    return (
      <section className="border p-6 mb-6" style={{ borderColor: "var(--border)" }} data-testid="daily-briefing-loading">
        <div className="overline flex items-center gap-2"><Sun weight="bold" style={{ color: "var(--accent)" }} /> // daily briefing</div>
        <div className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>Composing your morning…</div>
      </section>
    );
  }

  if (brief?.error) return null;
  if (!brief) return null;

  return (
    <section className="border p-6 mb-6" style={{ borderColor: "var(--border)", background: "linear-gradient(180deg, rgba(0,210,255,0.03), transparent)" }} data-testid="daily-briefing">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="overline flex items-center gap-2"><Sun weight="bold" style={{ color: "var(--accent)" }} /> // daily briefing</div>
          <h2 className="mt-2 text-xl lg:text-2xl font-bold" data-testid="briefing-greeting">{brief.greeting}</h2>
          {brief.counts && (
            <div className="mt-2 text-sm flex flex-wrap gap-x-4 gap-y-1" style={{ color: "var(--text-2)" }} data-testid="briefing-counts">
              {brief.counts.replies > 0 && <span>{brief.counts.replies} reply{brief.counts.replies === 1 ? "" : "s"}</span>}
              {brief.counts.meetings > 0 && <span>{brief.counts.meetings} meeting{brief.counts.meetings === 1 ? "" : "s"}</span>}
              {brief.counts.overdue_tasks > 0 && <span style={{ color: "#FF3B30" }}>{brief.counts.overdue_tasks} overdue</span>}
              {brief.counts.today_tasks > 0 && <span>{brief.counts.today_tasks} due today</span>}
              {brief.counts.dormant > 0 && <span>{brief.counts.dormant} dormant</span>}
            </div>
          )}
        </div>
        <button
          onClick={() => load(true)}
          disabled={reloading}
          className="btn-secondary text-xs px-2"
          title="Refresh briefing"
          data-testid="refresh-briefing-btn"
        >
          <ArrowClockwise weight="bold" className={reloading ? "animate-spin" : ""} />
        </button>
      </div>

      {(brief.actions || []).length === 0 ? (
        <div className="mt-4 text-sm" style={{ color: "var(--text-2)" }} data-testid="briefing-empty">
          No actions queued. A great moment to reach out to a dormant contact.
        </div>
      ) : (
        <div className="mt-5 space-y-2" data-testid="briefing-actions">
          {brief.actions.map((a, idx) => {
            const meta = KIND_META[a.kind] || { label: a.kind, color: "var(--text-3)", icon: Clock };
            const Icon = meta.icon;
            const linkTo = a.contact_id ? `/contacts/${a.contact_id}` : "/tasks";
            return (
              <Link
                key={`${a.kind}-${idx}`}
                to={linkTo}
                className="block border p-3 hover:bg-[#15303A] transition"
                style={{ borderColor: "var(--border)" }}
                data-testid={`briefing-action-${idx}`}
              >
                <div className="flex items-center gap-3">
                  <Icon weight="bold" style={{ color: meta.color, fontSize: 20 }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">
                      {a.contact_name || a.reason}
                    </div>
                    <div className="overline mt-1 truncate">
                      <span style={{ color: meta.color }}>{meta.label}</span>
                      {" · "}{a.reason}
                    </div>
                    {a.recommended && (
                      <div className="text-sm mt-1" style={{ color: "var(--text-2)" }}>
                        recommended: {a.recommended}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
